import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import hall from "@/assets/sim/sim-hall-wide.jpg";
import PracticeBar, { PracticeToggle } from "@/components/practice/PracticeBar";

/** Bronze-coin back button used on every themed side page. */
export function BackCoin({ to, onClick, label }: { to?: string; onClick?: () => void; label: string }) {
  const cls = "group inline-flex items-center gap-2 h-10 pl-1.5 pr-4 rounded-full border-2 border-gold shadow-md text-sm font-semibold text-gold-dark transition-transform hover:-translate-y-0.5";
  const style = { background: "linear-gradient(hsl(var(--card)), hsl(var(--gold) / 0.25))" };
  const inner = (
    <>
      <span className="grid place-items-center h-7 w-7 rounded-full border border-gold-light/70 text-gold-light" style={{ background: "radial-gradient(circle, hsl(var(--bronze)), hsl(var(--bronze-dark)))" }}>
        <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
      </span>
      {label}
    </>
  );
  return to ? <Link to={to} className={cls} style={style}>{inner}</Link> : <button onClick={onClick} className={cls} style={style}>{inner}</button>;
}

/** Top row + hall banner with a painted medallion and title. */
export default function ThemedPageHeader({ title, subtitle, icon, back, backLabel, practice }: {
  title: string; subtitle?: string; icon: string; back: string | (() => void); backLabel: string; practice?: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {typeof back === "string" ? <BackCoin to={back} label={backLabel} /> : <BackCoin onClick={back} label={backLabel} />}
        <div className="ml-auto flex items-center gap-2">
          {practice && <PracticeToggle />}
          <span className="hidden sm:inline font-display font-bold tracking-[0.12em] text-gold-dark text-sm">❦ DECORUM LIFESTYLE</span>
        </div>
      </div>
      {practice && <PracticeBar />}
      <section className="relative overflow-hidden rounded-2xl border-2 border-gold/60 shadow-md" style={{ height: "clamp(130px,16vw,230px)" }}>
        <img src={hall} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, hsl(var(--card)) 0%, hsl(var(--card) / 0.95) 45%, hsl(var(--card) / 0.2) 85%)" }} />
        <div className="relative h-full flex items-center gap-4 sm:gap-6 px-4 sm:px-10">
          <div className="shrink-0 h-16 w-16 sm:h-24 sm:w-24 rounded-full border-2 border-gold shadow-lg overflow-hidden grid place-items-center" style={{ background: "radial-gradient(circle, hsl(var(--bronze)), hsl(var(--bronze-dark)))" }}>
            <img src={icon} alt="" className="h-[85%] w-[85%] object-contain" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-5xl text-gold-dark leading-tight" style={{ fontVariant: "small-caps" }}>{title}</h1>
            {subtitle && <p className="mt-1 text-[10px] sm:text-xs tracking-[0.25em] uppercase text-gold-dark/90">{subtitle}</p>}
          </div>
        </div>
      </section>
    </div>
  );
}
